import FuseCountDownButton from "components/utils/FuseCountDownButton";
import { animate, AnimatePresence, motion } from "framer-motion";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { ImLoop2 } from "react-icons/im";
import { MdCheck } from "react-icons/md";

interface UnsavedCodeAlertPopUpProps {
  ref?:any;
  onBgClick?: () => void;
  onSave: () => void;
  onClosePopUp: () => void;
  onCloseWithoutSaving: () => void;
}

const defaultBgVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const defaultPanelVariants = {
  initial: { opacity: 0, y: "100%" },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, type: "spring" } },
  exit: {
    opacity: 0,
    y: "100%",
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const UnsavedCodeAlertPopUp: React.FC<UnsavedCodeAlertPopUpProps> = forwardRef(
  ({ onBgClick = () => {}, onSave, onClosePopUp, onCloseWithoutSaving }, ref) => {
    // States definition
    const [status, setStatus] = useState<"waiting" | "saving" | "saved">(
      "waiting"
    );
    const [timer, setTimer] = useState(100);

    /*
     * To control the setTriggerTimer.
     */
    useImperativeHandle(ref, () => ({
      setSavedStatus: () => {
        setStatus("saved");
      },
    }));

    // Define the title based on the status
    const title =
      status === "saved"
        ? "Changes saved"
        : status === "saving"
        ? "Saving changes..."
        : "You have unsaved changes";

    // Define the paragraphs based on the status
    const paragraphs =
      status === "saved"
        ? ["All changes have been saved successfully "]
        : status === "saving"
        ? ["Hang tight"]
        : [
            "Are you sure you want to navigate away?",
            "Any changes you have will not be saved",
          ];

    /**
     * Function that controls the progress bar animation
     * on the dismiss step (stage 3)
     */
    useEffect(() => {
      if (status === "saved") {
        const controls = animate(100, 0, {
          duration: 5,
          ease: "linear",
          onUpdate: (latest: number) => {
            // Set the value of the timer
            setTimer(latest);
          },
          onComplete: () => onCloseWithoutSaving(),
        });
        return () => controls.stop();
      }
    }, [status]);

    const handleSaveClick = () => {
      // Prevent executing the function when it's saving or saved
      if (["saved", "saving"].includes(status)) return;

      // Move into the saving state
      setStatus("saving");

      // And execute the save click function
      onSave();
    };

    return (
      <>
        <motion.div
          key="saving-blurred-background"
          className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-50 flex flex-col flex-grow"
          variants={defaultBgVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
          onClick={onBgClick}
        />
        <motion.div
          key="add-contact"
          className="fixed w-[462px] h-fit left-0 right-0 ml-auto mr-auto bottom-5 p-6 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-50"
          variants={defaultPanelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
        >
          <div
            key="loading"
            className="inline-flex w-full max-h-[80vh] flex-col flex-1 justify-start items-center gap-5 overflow-y-auto custom-scroll"
          >
            <div className="self-stretch py-8 justify-center items-center inline-flex">
              <div className="relative size-20 overflow-hidden">
                <AnimatePresence mode="popLayout">
                  {status === "saved" ? (
                    <motion.div
                      key="saved-state"
                      initial={{ y: "100%" }}
                      animate={{ y: 0 }}
                      className="absolute inset-0 rounded-full size-[80px] bg-success-500 inline-flex items-center justify-center"
                    >
                      <MdCheck className="size-14 fill-white" />
                    </motion.div>
                  ) : status === "saving" ? (
                    <motion.div
                      key="saving-state"
                      initial={{ y: "100%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "-100%" }}
                      className="absolute inset-0 rounded-full size-[80px] bg-skb-700 inline-flex items-center justify-center"
                    >
                      <motion.div
                        key="rotating-loop"
                        animate={{ rotate: [0, 90, 180, 270, 360] }}
                        transition={{
                          repeat: Infinity,
                          duration: 4,
                          type: "easeOut",
                          times: [
                            0,
                            0.15, // stay at 0
                            0.25,
                            0.4, // stay at 90
                            0.5,
                            0.65, // stay at 180
                            0.75,
                            0.9, // stay at 270
                            1, // back to 360 (same as 0)
                          ],
                        }}
                        className="size-9"
                      >
                        <ImLoop2 className="size-full fill-white" />
                      </motion.div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="unsave-state"
                      initial={{ y: 0 }}
                      animate={{ y: 0 }}
                      exit={{ y: "-100%" }}
                      className="absolute inset-0 rounded-full size-[80px] bg-warning-500 inline-flex items-center justify-center"
                    >
                      <div className="text-white font-sans text-6xl select-none">
                        !
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <div className="w-full h-fit flex-col justify-start items-start gap-6 inline-flex">
              <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
                <div className="self-stretch text-neutral-700 text-[23px] leading-7 font-semibold font-sans justify-start select-none">
                  {title}
                </div>
                {/* Showing the paragraphs */}
                {paragraphs &&
                  paragraphs.length > 0 &&
                  paragraphs.map((text: string, index: number) => (
                    <div
                      key={index}
                      className="self-stretch text-neutral-700 text-base font-normal font-sans leading-7 select-none"
                    >
                      {text}
                    </div>
                  ))}
                {/* Buttons */}
                {["waiting", "saving"].includes(status) ? (
                  // Action buttons
                  <div className="self-stretch inline-flex flex-col justify-center items-center gap-4">
                    <button
                      className={`self-stretch h-12 px-5 py-3 ${
                        status === "waiting"
                          ? "bg-secondary-600 hover:bg-secondary-700 text-white ease-in-out duration-150"
                          : "bg-Subtle text-neutral-400 cursor-not-allowed"
                      } rounded-md inline-flex justify-center items-center`}
                      onClick={handleSaveClick}
                    >
                      <div className="justify-start text-lg font-semibold font-sans leading-snug select-none">
                        Save and close
                      </div>
                    </button>
                    <div className="self-stretch h-12 px-5 py-3 inline-flex justify-center items-center relative overflow-hidden">
                      <AnimatePresence mode="sync">
                        {status === "waiting" ? (
                          <motion.button
                            key="close-without-saving-button"
                            initial={{ opacity: 1, x: 0 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: "100%" }}
                            transition={{ duration: 0.3, type: "spring" }}
                            className="absolute inset-0 justify-start text-secondary-600 hover:text-secondary-700 text-lg font-semibold font-sans leading-snug select-none"
                            onClick={onCloseWithoutSaving}
                          >
                            Close without saving
                          </motion.button>
                        ) : (
                          <motion.button
                            key="cancel-button"
                            initial={{ opacity: 1, x: "-100%" }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: "100%" }}
                            transition={{ duration: 0.3, type: "spring" }}
                            className="absolute inset-0 justify-start text-secondary-600 hover:text-secondary-700 text-lg font-semibold font-sans leading-snug select-none"
                            onClick={onClosePopUp}
                          >
                            Cancel
                          </motion.button>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                ) : (
                  // Count down buttons
                  <FuseCountDownButton
                    key="saved-count-down"
                    text="Got it"
                    timer={timer}
                    onClick={onCloseWithoutSaving}
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </>
    );
  }
);

export default UnsavedCodeAlertPopUp;
